import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable ,  BehaviorSubject } from 'rxjs';
import { environment } from '../../environments/environment';
import { USER_KEY } from '../constants/auth-constants';

@Injectable()
export class ApisService {
  debugMode = new BehaviorSubject<boolean>(false);
  userData = new BehaviorSubject<any>({ access_token: null, refresh_token: null, user_id: null });
  fixtureData = new BehaviorSubject<any>({ index: null, courseName: null, fixtureDate: null });

  observableDebugMode = this.debugMode.asObservable();
  observableFixture = this.fixtureData.asObservable();
  observableLoggedIn = this.userData.asObservable();

  loggedinHeaders: HttpHeaders;

  constructor(private http: HttpClient) {}

  setHeaders(access_token): void {
    this.loggedinHeaders = new HttpHeaders({
      Authorization: `Bearer ${access_token}`
    });
  }

  // Content Loading
  getFixtures = (date): any => {
    return this.http.get(`${environment.apiBase}/cos/v1/fixtures?ondate=${date}`, { headers: this.loggedinHeaders });
  }

  getRaces = (year, fixtureId): any => {
    return this.http.get(`${environment.apiBase}/cos/v1/fixtures/${year}/${fixtureId}/races`, { headers: this.loggedinHeaders });
  }

  getSilk(colourDescription: string, entryColourCode: number, nonRunner: boolean): Observable<any> {
    let url = `${environment.silkBase}?id=${entryColourCode}&desc=${colourDescription}`;
    if (nonRunner) {
      url += '&nonrunner';
    }
    url += `&size=36&type=base64`;
    return this.http.get(url);
  }

  getRaceConditions = (year, raceId, divSeq): any => {
    return this.http.get(`${environment.apiBase}/cos/v1/races/${year}/${raceId}/${divSeq}/conditions`, { headers: this.loggedinHeaders });
  }

  updateRace = (year, raceId, divSeq, payload): any => {
    return this.http
      .patch(`${environment.apiBase}/cos/v1/races/${year}/${raceId}/${divSeq}`, payload, { headers: this.loggedinHeaders });
  }

  updateJockeyClaim = (year, raceId, divSeq, animalId, payload): any => {
    return this.http
    .patch(`${environment.apiBase}/cos/v1/entries/${year}/${raceId}/${divSeq}/${animalId}`, payload, { headers: this.loggedinHeaders });
  }

  updateWeighPenalty = (year, raceId, divSeq, animalId, payload): any => {
    return this.http
    .patch(`${environment.apiBase}/cos/v1/entries/${year}/${raceId}/${divSeq}/${animalId}`, payload, { headers: this.loggedinHeaders });
  }

  changeJockey = (year, raceId, divSeq, animalId, payload): any => {
    return this.http
    .patch(`${environment.apiBase}/cos/v1/entries/${year}/${raceId}/${divSeq}/${animalId}`, payload, { headers: this.loggedinHeaders });
  }

  updateDisqualified = (year, raceId, divSeq, animalId, payload): any => {
    return this.http
      .patch(`${environment.apiBase}/cos/v1/entries/${year}/${raceId}/${divSeq}/${animalId}`, payload, { headers: this.loggedinHeaders });
  }

  updateWithdrawn = (year, raceId, divSeq, animalId, payload): any => {
    return this.http
      .patch(`${environment.apiBase}/cos/v1/entries/${year}/${raceId}/${divSeq}/${animalId}`, payload, { headers: this.loggedinHeaders });
  }

  updatePosition = (year, raceId, divSeq, animalId, payload): any => {
    return this.http
      .patch(`${environment.apiBase}/cos/v1/entries/${year}/${raceId}/${divSeq}/${animalId}`, payload, { headers: this.loggedinHeaders });
  }

  resetDisqualified = (year, raceId, divSeq) => {
    return this.http
        .delete(`${environment.apiBase}/cos/v1/races/${year}/${raceId}/${divSeq}/disqualifications`, { headers: this.loggedinHeaders });
  }

  updateRunnerComments = (year, raceId, divSeq, animalId, payload): any => {
    return this.http
        .post(`${environment.apiBase}/cos/v1/entries/${year}/${raceId}/${divSeq}/${animalId}/comments`,
        payload, { headers: this.loggedinHeaders });
  }

  updateJockeyWeights = (year, raceId, divSeq, animalId, payload): any => {
    return this.http
        .post(`${environment.apiBase}/cos/v1/entries/${year}/${raceId}/${divSeq}/${animalId}/weights`,
        payload, { headers: this.loggedinHeaders });
  }

  updateGearChecks = (year, raceId, divSeq, animalId, payload): any => {
    return this.http
        .post(`${environment.apiBase}/cos/v1/entries/${year}/${raceId}/${divSeq}/${animalId}/headgears`,
        payload, { headers: this.loggedinHeaders });
  }

  updateRaceClaim = (payload): any => {
    return this.http.post(`${environment.apiBase}/cos/v1/claims`, payload, { headers: this.loggedinHeaders });
  }

  deleteRaceClaim = (claimId): any => {
    return this.http.delete(`${environment.apiBase}/cos/v1/claims/${claimId}`, { headers: this.loggedinHeaders });
  }

  // Utilities
  getNonRunnerReasons = (): any => {
    return this.http.get(`${environment.apiBase}/cos/v1/lists?typeAlias=nonrunner`, { headers: this.loggedinHeaders });
  }

  getGroundDescription(id: Number): {id: Number, label: String} {
    const grounds = [
      // TURF:
      { id:  1, label: 'Hard', short: 'H'},
      { id:  2, label: 'Firm', short: 'F'},
      { id:  3, label: 'Good to Firm', short: 'G/F'},
      { id:  4, label: 'Good', short: 'G'},
      { id:  5, label: 'Good to Soft', short: 'G/S'},
      { id:  6, label: 'Soft', short: 'S'},
      { id:  7, label: 'Heavy', short: 'HY'},
      // AWT:
      { id: 10, label: 'Standard', short: 'ST'},
      { id: 8,  label: 'Fast', short: 'FS'},
      { id: 12, label: 'Slow', short: 'SL'},
      { id: 29, label: 'Standard to Fast', short: 'ST/FS'},
      { id: 30, label: 'Standard to Slow', short: 'ST/SL'},
      // BOTH:
      { id: 23, label: 'Frozen', short: 'FROZ'},
      // UNUSED:
      {id: 20, label: 'Yielding', short: 'Y'},
      {id: 21, label: 'Holding', short: 'HG'},
      {id: 22, label: 'Snow', short: 'SNOW'},
      {id: 24, label: 'Muddy', short: 'MUD'},
      {id: 25, label: 'Sloppy', short: 'SLOP'},
      {id: 26, label: 'Wet Fast', short: 'WFAST'},
      {id: 27, label: 'Sand', short: 'SAND'},
      {id: 28, label: 'Good to Yielding', short: 'G/Y'},
      {id: 31, label: 'Dead', short: 'D'},
      {id: 32, label: 'Wet', short: 'WET'},
      {id: 33, label: 'Easy', short: 'EASY'},
      {id: 34, label: 'Soft to Heavy', short: 'S/HY'},
      {id: 35, label: 'Yielding to Soft', short: 'Y/S'}
    ];
    if (typeof id === 'number') {
      return grounds.find(g => g.id === id);
    } else {
      return {id: Number(id), label: 'Unknown'};
    }
  }

  getCommentTypes(): Array<any> {
    return [
      { id: '207', label: 'Other' },
      { id: '212', label: 'Riding Fees' },
      { id: '201', label: 'Colours' },
      { id: '211', label: 'Fines' },
      { id: '202', label: 'Sponsorship' }
    ];
  }

  getJockeyList = (query, jockey, noFilter): Observable<any> => {
    return this.http.get(
      `${environment.apiBase}/cos/v1/entries/${jockey.yearOfRace}/${jockey.raceId}/${jockey.divisionSequence}/${jockey.animalId}/jockeys?` +
        `name=${query}` +
        `&noFilter=${noFilter ? '1' : '0'}`,
      { headers: this.loggedinHeaders }
    );
  }

  getFinishingCodes(): Array<any> {
    return [
      { id: 1, label: 'Pulled Up' },
      { id: 2, label: 'Fell' },
      { id: 3, label: 'Unseated Rider' },
      { id: 4, label: 'Ran Out' },
      { id: 5, label: 'Refused' },
      { id: 6, label: 'Brought Down' },
      { id: 7, label: 'Carried Out' },
      { id: 8, label: 'Slipped Up' },
      { id: 9, label: 'Walkover' },
      { id: 23, label: 'Refused to Run' }
      // {id: 13, label: 'Disqualified'},
      // {id: 14, label: 'Disqualified Placed Second'},
      // {id: 15, label: 'Disqualified Placed Last'},
      // {id: 20, label: 'Withdrawn NOT Under Order'},
      // {id: 21, label: 'Withdrawn Under Orders'},
      // {id: 22, label: 'Unplaced'}
    ];
  }

  getDisqualifiedReasons(): Array<any> {
    return [
      { id: 13, label: 'Disqualified' },
      { id: 14, label: 'Disqualified Placed Second' },
      { id: 15, label: 'Disqualified Placed Last' }
    ];
  }

  // Observables Update
  changeDebugMode(data): any {
    this.debugMode.next(data);
  }

  changeFixtureData = (data): any => {
    this.fixtureData.next(data);
  }

  changeUserData = (data): any => {
    this.userData.next(data);
  }

  public updateJockeyRoomLeave = (year, raceId, divSeq, payload): any => {
    return this.http.patch(
      `${environment.apiBase}/cos/v1/race/${year}/${raceId}/${divSeq}/leave`,
      payload,
      { headers: this.loggedinHeaders }
    );
  }

  public isResultAmended = (year, raceId, divSeq): any => {
    return this.http.get(
      `${environment.apiBase}/cos/v1/race/${year}/${raceId}/${divSeq}/result-amended`,
      { headers: this.loggedinHeaders }
    );
  }

  retrieveToken = (exchangeToken): any => {
    return this.http.post(`${environment.apiBase}/auth/retrieve-token`, { 'exchange_token': exchangeToken });
  }

  saveInSession(userDetails): Boolean {
    try {
      sessionStorage.setItem(USER_KEY, JSON.stringify(userDetails));
      return true;
    } catch (e) {
      throw e;
    }
  }

  getFromSession = (): any => {
    return JSON.parse(sessionStorage.getItem(USER_KEY));
  }

  logout = (): void => {
    sessionStorage.removeItem(USER_KEY);
    window.location.href = `${ environment.authUrl }/${ environment.tenantId }/logout`;
  }
}
